import { motion } from "framer-motion";
import { MapPin, Clock, Lock, Check } from "lucide-react";
import { categoryLabel, stateAbbr, formatEventDate } from "../lib/categories";

export function EventCard({ event, onBook, onCancel, busy, index = 0 }) {
  const booked = event.is_booked;
  const mine = event.mine;

  const daysOut = (() => {
    try {
      const ev = new Date(event.event_date + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return Math.round((ev - today) / 86400000);
    } catch {
      return 999;
    }
  })();
  const lockedForRelease = daysOut < 3;

  return (
    <motion.div
      data-testid={`event-card-${event.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: (index % 6) * 0.05 }}
      className={`relative bg-[#22303C] border border-white/10 p-6 flex flex-col ${
        booked && !mine ? "opacity-50" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="overline text-[hsl(var(--primary))]">
          {categoryLabel(event.category)}
        </span>
        {event.state && (
          <span
            data-testid={`event-state-${event.id}`}
            className="overline text-white/50 border border-white/15 px-2 py-1"
          >
            {stateAbbr(event.state)}
          </span>
        )}
      </div>

      {booked && (
        <div className="flex justify-end -mt-2 mb-2">
          <span
            data-testid={`event-status-${event.id}`}
            className={`flex items-center gap-1 overline ${
              mine ? "text-white" : "text-white/50"
            }`}
          >
            <Lock className="w-3.5 h-3.5" /> {mine ? "Yours" : "Taken"}
          </span>
        </div>
      )}

      <h3 className="font-heading font-extrabold text-xl leading-tight mb-3">
        {event.title}
      </h3>

      <div className="space-y-2 text-sm text-white/60 mb-4">
        <div className="font-heading font-bold text-white text-base">
          {formatEventDate(event.event_date)}
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 shrink-0" />
          {event.start_time}
          {event.end_time ? ` – ${event.end_time}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 shrink-0" />
          {event.location}
        </div>
      </div>

      {event.description && (
        <p className="text-sm text-white/50 leading-relaxed mb-5 line-clamp-3">
          {event.description}
        </p>
      )}

      <div className="mt-auto pt-2">
        {!booked && (
          <button
            data-testid={`book-btn-${event.id}`}
            onClick={() => onBook(event)}
            disabled={busy}
            className="w-full bg-[hsl(var(--primary))] text-white overline py-3 hard-shadow-hover disabled:opacity-50"
          >
            {busy ? "Claiming…" : "Book This Event"}
          </button>
        )}
        {booked && mine && (
          <div>
            <div className="flex items-center gap-2">
              <span className="flex-1 flex items-center justify-center gap-1.5 border border-white/20 text-white overline py-3">
                <Check className="w-4 h-4 text-[hsl(var(--primary))]" /> Booked By You
              </span>
              {!lockedForRelease && (
                <button
                  data-testid={`cancel-btn-${event.id}`}
                  onClick={() => onCancel(event)}
                  disabled={busy}
                  className="border border-white/20 text-white/60 hover:text-white hover:border-white/50 overline px-4 py-3 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
            {lockedForRelease && (
              <p
                data-testid={`release-locked-${event.id}`}
                className="flex items-center gap-1.5 text-white/40 text-xs mt-2 leading-relaxed"
              >
                <Lock className="w-3 h-3 shrink-0" />
                Locked — can't cancel 3 days prior to the event. Contact the admin if you can't attend.
              </p>
            )}
          </div>
        )}
        {booked && !mine && (
          <div className="w-full border border-white/10 text-white/40 overline py-3 text-center">
            Event Taken
          </div>
        )}
      </div>
    </motion.div>
  );
}
